/**
 * Campaign params read off the HOST page URL and forwarded to the iframe.
 *
 * Without this, everything entering through the embedded SDK reached the
 * backend with no attribution: the ad click lands on the merchant's page, the
 * SDK builds its own iframe URL, and `utm_*` never crossed over. Only our own
 * campaign landings kept them, because there the iframe URL *is* the ad's
 * landing URL.
 *
 * Reading the URL at mount time is not enough on its own. The click lands on
 * the merchant's home or campaign page, the visitor then navigates to a model
 * page, and the site drops the params on the way — by the time the brick
 * mounts, `location.search` is clean. Measured on the first day the forwarding
 * shipped: 88 SDK sessions, 0 with a campaign. So the params are captured when
 * the SCRIPT loads (`finva.js` sits on every page of the merchant's site, not
 * only where the brick mounts) and parked in `sessionStorage`, which the mount
 * then falls back to.
 *
 * Session-scoped on purpose: `localStorage` would keep attributing visits days
 * later to an ad the visitor no longer came from.
 *
 * The iframe already reads these (`lib/credit-application/utm.ts`) and sends
 * them with the registration payload. They travel as query params — never in
 * `InitPayload`, whose contract is frozen.
 *
 * Parsed by hand instead of with `URLSearchParams` to keep the bundle small,
 * same reasoning as `encodeQueryParam` in `util/dom`.
 */

/**
 * Keys forwarded, in URL order. The five `utm_*` mirror `UTM_KEYS` in
 * `types/credit-application` — keep both in sync. The click ids are not read as
 * structured fields; they ride along so the raw click is still recoverable from
 * `other_url_params`.
 */
export const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
] as const;

export type CampaignKey = (typeof CAMPAIGN_KEYS)[number];
export type CampaignParams = Partial<Record<CampaignKey, string>>;

/** Namespaced so it cannot collide with whatever the merchant's site stores. */
export const CAMPAIGN_STORAGE_KEY = 'finva:campaign';

/** Same cap the iframe applies when reading them (`UTM_MAX_LEN`); more would be cut there anyway. */
const UTM_MAX_LEN = 255;

/**
 * Click ids are opaque and long (a real `fbclid` runs past 250 chars), so
 * truncating one corrupts it. The cap is only a guard against a junk URL.
 */
const CLICK_ID_MAX_LEN = 512;

function maxLenFor(key: CampaignKey): number {
  return key === 'fbclid' || key === 'gclid' ? CLICK_ID_MAX_LEN : UTM_MAX_LEN;
}

/**
 * Returns the campaign params present in `search` (defaults to the host page's
 * query). Absent keys are omitted, so a page with no campaign params produces
 * an iframe URL byte-identical to the previous one.
 *
 * A repeated param takes the first value, matching how the iframe reads them.
 */
export function readCampaignParams(search?: string): CampaignParams {
  const raw =
    search ?? (typeof window !== 'undefined' && window.location ? window.location.search : '');
  const out: CampaignParams = {};
  if (!raw) return out;

  for (const pair of raw.replace(/^\?/, '').split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    if (eq < 0) continue;
    const key = decodeParam(pair.slice(0, eq)) as CampaignKey;
    if (!CAMPAIGN_KEYS.includes(key) || out[key] !== undefined) continue;
    // `+` is a space in query strings; `decodeURIComponent` alone leaves it.
    const value = decodeParam(pair.slice(eq + 1).replace(/\+/g, ' ')).trim();
    if (value) out[key] = value.slice(0, maxLenFor(key));
  }
  return out;
}

/** A malformed escape (`%zz`) throws in `decodeURIComponent`; keep the raw text. */
function decodeParam(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * `sessionStorage` is not always reachable — Safari private mode, sandboxed
 * frames and cookie-blocking settings throw on mere property access, not just
 * on read/write. This runs while the merchant's page is loading, so anything
 * that escapes here would take their page down with it. Never throws.
 */
function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** Re-validates what comes back: stored text is not trusted more than a URL. */
function readStored(): CampaignParams {
  const store = storage();
  if (!store) return {};
  try {
    const raw = store.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: CampaignParams = {};
    for (const key of CAMPAIGN_KEYS) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value) out[key] = value.slice(0, maxLenFor(key));
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Captures the campaign of the page currently loading. Called once when the
 * bundle evaluates, so it runs on the ad's landing page even when no brick
 * mounts there.
 *
 * Only writes when the URL actually carries a campaign: on the later pages of
 * the visit the query is clean, and clearing there would undo the capture we
 * came for. A fresh campaign overwrites the previous one — within a session the
 * last ad clicked is the one that brought the visitor to this application.
 */
export function captureCampaignParams(): CampaignParams {
  const current = readCampaignParams();
  if (Object.keys(current).length === 0) return readStored();

  const store = storage();
  if (store) {
    try {
      store.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(current));
    } catch {
      // Quota or a blocked store: the visit still works, we just lose the
      // campaign if the visitor navigates before opening the widget.
    }
  }
  return current;
}

/**
 * The campaign to forward to the iframe: what the current URL carries, or what
 * was captured earlier in the session. Used at mount time.
 */
export function getCampaignParams(): CampaignParams {
  const current = readCampaignParams();
  return Object.keys(current).length > 0 ? current : readStored();
}

// Capture on script load, not on mount — by mount time the merchant's own
// navigation has usually stripped the params. Same reasoning as
// `SDK_SCRIPT_ORIGIN` in `core/finva`: the value only exists this early.
captureCampaignParams();
